import { useCallback, useState } from 'react'
import { createDeltaOperationId } from '../../services/stripboardDeltaService'
import { useTimeline } from '../../context/useTimeline'
import { useAuth } from '../../context/useAuth'
import { useDisplayName } from '../../hooks/useDisplayName'
import { writeChecklistUpdate } from '../../services/checklistWriter'
import { addDays, fromIsoDate, toIsoDate, formatTimeMinutes, parseTimeMinutes } from './calendarHelpers'
import type { CalendarEvent } from './calendarHelpers'

export type DragMode = 'move' | 'resize-start' | 'resize-end'

interface ActiveDrag {
  event: CalendarEvent
  mode: DragMode
  /** ISO date the pointer started from. */
  anchorIso: string
  /** Optional anchor minute-of-day for time-grid resizing. */
  anchorMinutes?: number
}

export interface DragOverrides {
  startDate?: string
  endDate?: string
  startTime?: string
  endTime?: string
}

export interface DragReschedulerApi {
  active: ActiveDrag | null
  overrides: Record<string, DragOverrides>
  beginDrag: (event: CalendarEvent, mode: DragMode, anchorIso: string, anchorMinutes?: number) => void
  updateDrag: (currentIso: string, currentMinutes?: number) => void
  cancelDrag: () => void
  commitDrag: () => Promise<void>
}

const clampIso = (a: string, b: string) => (a.localeCompare(b) <= 0 ? a : b)
const maxIso = (a: string, b: string) => (a.localeCompare(b) >= 0 ? a : b)

const computeOverride = (active: ActiveDrag, currentIso: string, currentMinutes?: number): DragOverrides => {
  const startA = fromIsoDate(active.event.startDate)
  const endA = fromIsoDate(active.event.endDate)
  const deltaDays = Math.round(
    (fromIsoDate(currentIso).getTime() - fromIsoDate(active.anchorIso).getTime()) / 86_400_000,
  )

  let next: DragOverrides = {}
  if (active.mode === 'move') {
    next = {
      startDate: toIsoDate(addDays(startA, deltaDays)),
      endDate: toIsoDate(addDays(endA, deltaDays)),
    }
  } else if (active.mode === 'resize-start') {
    const newStart = toIsoDate(addDays(startA, deltaDays))
    next = { startDate: clampIso(newStart, active.event.endDate), endDate: active.event.endDate }
  } else if (active.mode === 'resize-end') {
    const newEnd = toIsoDate(addDays(endA, deltaDays))
    next = { startDate: active.event.startDate, endDate: maxIso(newEnd, active.event.startDate) }
  }

  if (
    active.event.startTime &&
    active.event.endTime &&
    typeof currentMinutes === 'number' &&
    typeof active.anchorMinutes === 'number'
  ) {
    const minutesDelta = currentMinutes - active.anchorMinutes
    const startMins = parseTimeMinutes(active.event.startTime) ?? 0
    const endMins = parseTimeMinutes(active.event.endTime) ?? startMins + 60
    if (active.mode === 'move') {
      next.startTime = formatTimeMinutes(Math.max(0, Math.min(24 * 60 - 15, startMins + minutesDelta)))
      next.endTime = formatTimeMinutes(Math.max(15, Math.min(24 * 60, endMins + minutesDelta)))
    } else if (active.mode === 'resize-start') {
      const nextStart = Math.max(0, Math.min(endMins - 15, startMins + minutesDelta))
      next.startTime = formatTimeMinutes(nextStart)
      next.endTime = active.event.endTime
    } else if (active.mode === 'resize-end') {
      const nextEnd = Math.max(startMins + 15, Math.min(24 * 60, endMins + minutesDelta))
      next.endTime = formatTimeMinutes(nextEnd)
      next.startTime = active.event.startTime
    }
  }

  return next
}

export const useDragReschedule = (): DragReschedulerApi => {
  const { updateElement, checklistItems, applyStripboardDelta } = useTimeline()
  const { currentUser, userProfile } = useAuth()
  const displayName = useDisplayName(userProfile ?? currentUser)
  const [active, setActive] = useState<ActiveDrag | null>(null)
  const [overrides, setOverrides] = useState<Record<string, DragOverrides>>({})

  const beginDrag = useCallback((event: CalendarEvent, mode: DragMode, anchorIso: string, anchorMinutes?: number) => {
    setActive({ event, mode, anchorIso, anchorMinutes })
    setOverrides({})
  }, [])

  const updateDrag = useCallback((currentIso: string, currentMinutes?: number) => {
    setActive((current) => {
      if (!current) return current
      setOverrides({ [current.event.id]: computeOverride(current, currentIso, currentMinutes) })
      return current
    })
  }, [])

  const cancelDrag = useCallback(() => {
    setActive(null)
    setOverrides({})
  }, [])

  const commitDrag = useCallback(async () => {
    if (!active) {
      cancelDrag()
      return
    }
    const next = overrides[active.event.id]
    if (!next) {
      cancelDrag()
      return
    }

    try {
      if (active.event.elementId) {
        const payload: Record<string, string> = {}
        if (next.startDate) payload.startDate = next.startDate
        if (next.endDate) payload.endDate = next.endDate
        if (Object.keys(payload).length > 0) {
          await updateElement(active.event.elementId, payload)
        }
      } else if (active.event.checklistItemId && active.event.parentElementId) {
        const checklist = checklistItems.find((item) => item.id === active.event.checklistItemId)
        if (!checklist) throw new Error('Checklist item is no longer available.')
        if (next.startDate || next.endDate) {
          await applyStripboardDelta({
            operationId: createDeltaOperationId('calendar-dates', checklist.id),
            operationType: 'DATE_UPDATE',
            recordId: checklist.id,
            recordKind: 'CHECKLIST',
            sourceStatus: checklist.boardStatus,
            destinationStatus: checklist.boardStatus,
            expectedVersion: checklist.recordVersion,
            startDate: next.startDate ?? checklist.startDate ?? null,
            endDate: next.endDate ?? checklist.endDate ?? null,
          })
        }
        const timePayload: Record<string, string> = {}
        if (next.startTime) timePayload.scheduleTimeFrom = next.startTime
        if (next.endTime) timePayload.scheduleTimeTo = next.endTime
        if (Object.keys(timePayload).length > 0) {
          await writeChecklistUpdate(
            checklist.id,
            timePayload,
            { uid: currentUser?.uid ?? '', displayName: displayName || currentUser?.email || null },
          )
        }
      }
    } catch (error) {
      console.warn('[Calendar] drag-reschedule write failed', error)
    } finally {
      cancelDrag()
    }
  }, [active, applyStripboardDelta, cancelDrag, checklistItems, overrides, updateElement])

  return { active, overrides, beginDrag, updateDrag, cancelDrag, commitDrag }
}
